import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import { COLORS, Menu, Modal, typographyStyles } from '@livecommerce/design-system';

import ProfileImage from 'components-v3/ProfileImage/index.jsx';
import AccountSettings from 'components-v3/AccountSettings/index.jsx';
import CloseConfirmationDialog from 'components-v3/AccountSettings/CloseConfirmationDialog.jsx';
import { useFirebase } from 'components/Firebase/index.js';
import { useModal } from 'hooks/index.js';

import useProductDataOutsideRoutes from 'hooks/useProductDataOutsideRoutes.js';
import { CHAT_PRODUCT, ON_DEMAND_PRODUCT, ONE_TO_MANY_PRODUCT, ONE_TO_ONE_PRODUCT } from 'constants/products.js';
import { trackingService } from 'services/tracking/TrackingService.js';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import ContactUsModalAlt from 'components-v3/Help/ContactUsModalAlt.jsx';

import * as ROUTES from 'constants/routes.js';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import config from '../../client-config.js';
import { isStage } from 'constants/gcp';

export const { getBodyText } = typographyStyles;

const MenuItemButton = styled.button`
  display: flex;
  width: 100%;
  text-align: left;
  border: 0;
  border-radius: 6px;
  padding: 12px;
  white-space: nowrap;
  color: ${COLORS.dark_700};
  background-color: ${COLORS.white};
  ${getBodyText(14)};

  &:hover {
    background-color: ${COLORS.blackLight};
    color: ${COLORS.white};
  }
`;

const profileListStyles = css`
  padding: 0;
`;

const ProfileMenuItemButton = styled(MenuItemButton)`
  display: grid;
  grid-template-rows: 1fr 1fr;
  grid-template-columns: 44px 1fr;
  column-gap: 12px;
  min-width: 240px;
  cursor: pointer;

  .profile-menu__image {
    grid-column: 1 / 2;
    grid-row: 1 / 3;
  }

  .profile-menu__title {
    align-self: end;
    margin: 0;
    max-width: 176px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .profile-menu__subtitle {
    ${getBodyText(12, 500)};
    color: ${COLORS.greyMediumDark};
    margin: 0;
  }

  &:hover {
    .profile-menu__subtitle {
      color: ${COLORS.white};
    }

    .profile-menu__image {
      background-color: ${COLORS.black};
    }
  }
`;

const Container = styled.div`
  padding: 8px;
`;

const QRModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const QRDescription = styled.p`
  ${getBodyText(14, 400)};
`;
const QRCodeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
`;

export const MODAL_NAME = 'account-settings';

const ProfileMenu = ({ setIsOpened, ...props }) => {
  const { t } = useLocalization();
  const history = useHistory();

  const { features: { showContactSupportEnabled } = {} } = useFeatureContext();

  const { selectedProduct, orgId } = useProductDataOutsideRoutes();
  const { isOpen: isQRModalOpen, open: openQRModal, close: closeQRModal } = useModal();
  const { isOpen: isProfileModalOpen, open: openProfileModal, close: closeProfileModal } = useModal();
  const { isOpen: isConfirmDialogOpen, open: openConfirmDialog, close: closeConfirmDialog } = useModal();
  const { isOpen: isContactUsModalOpen, open: openContactUsModal, close: closeContactUsModal } = useModal();

  const [isClosable, setIsClosable] = useState(true);
  const firebase = useFirebase();
  const authUser = useSelector((state) => state.sessionState.authUser);

  const membership = authUser?.membership?.(orgId);

  const handleProfileButtonClick = () => {
    trackingService.track({
      eventType: 'profile-modal:open',
    });
    openProfileModal();
    setIsOpened(false);
  };

  const handleModalClose = () => {
    if (!isClosable) {
      openConfirmDialog();
      return;
    }
    trackingService.track({
      eventType: 'profile-modal:close',
    });
    closeProfileModal();
  };

  const handleConfirmCancelClick = () => {
    closeConfirmDialog();
  };

  const handleConfirmDiscardClick = () => {
    trackingService.track({
      eventType: 'profile-modal:close:without-saving',
    });
    closeConfirmDialog();
    closeProfileModal();
  };

  const { user } = membership || {};
  const userName = user?.displayName || authUser?.displayName || '';

  const helpLink = useMemo(() => {
    switch (selectedProduct) {
      case ONE_TO_MANY_PRODUCT:
        return 'https://knowledge.bambuser.com/live';
      case ONE_TO_ONE_PRODUCT:
        return 'https://knowledge.bambuser.com/videoconsultation';
      case ON_DEMAND_PRODUCT:
        return 'https://knowledge.bambuser.com/shoppable-video';
      default:
        return 'https://knowledge.bambuser.com';
    }
  }, [selectedProduct]);

  const downloadAppQrCodeUrl = useMemo(() => {
    const baseUrl = `${config.returnUrl}/applink/${isStage ? 'stage' : 'prod'}`;
    switch (selectedProduct) {
      case ONE_TO_MANY_PRODUCT:
      case ON_DEMAND_PRODUCT:
        return `${baseUrl}-${ONE_TO_MANY_PRODUCT}`;
      case ONE_TO_ONE_PRODUCT:
        return `${baseUrl}-${ONE_TO_ONE_PRODUCT}`;
      case CHAT_PRODUCT:
        return `${baseUrl}-${CHAT_PRODUCT}`;
      default:
        return null;
    }
  }, [selectedProduct]);

  const profileMenuItems = [
    (downloadAppQrCodeUrl && {
      label: t('globalHelp.downloadAppText'),
      action: () => {
        trackingService.track({
          eventType: 'profile-menu:download-app:click',
        });
        openQRModal();
      },
      dataTestId: 'downloadAppButton',
    }),
    {
      label: t('globalHelp.helpLinkText'),
      href: helpLink,
      action: () => {
        trackingService.track({
          eventType: 'profile-menu:knowledge-base:click',
        });
      },
      dataTestId: 'knowledgeBaseButton',
    },
    showContactSupportEnabled && {
      label: t('globalHelp.contactLinkText'),
      action: () => {
        trackingService.track({
          eventType: 'profile-menu:contact:click',
        });
        openContactUsModal();
        closeProfileModal();
      },
      dataTestId: 'contactSupportButton',
    },
    {
      label: t('globalHelp.logoutText'),
      variant: 'danger',
      action: () => {
        trackingService.track({
          eventType: 'profile-menu:sign-out:click',
        });
        if (localStorage.getItem('ssoSessionId')) {
          history.replace(ROUTES.buildRoute(ROUTES.SIGN_OUT, {}, { auto: 1 }));
        } else {
          firebase.doSignOut();
        }
        setIsOpened(false);
      },
      dataTestId: 'logoutButton',
    },
  ].filter(Boolean);

  return (
    <Container {...props}>
      {userName && (
        <>
          <ProfileMenuItemButton onClick={handleProfileButtonClick} data-test-id="accountSettingsMenuButton">
            <ProfileImage className="profile-menu__image" width={44} height={44} alt="User Avatar icon" />
            <p className="profile-menu__title" title={userName}>
              {userName}
            </p>
            <p className="profile-menu__subtitle">{t('profile.profileButtonDescriptionLabel')}</p>
          </ProfileMenuItemButton>
          <Modal
            width={1000}
            name={MODAL_NAME}
            isModalOpen={isProfileModalOpen}
            onClose={handleModalClose}
            title={t('profile.profileModalTitle')}
            dataTestId="accountSettingsModal"
          >
            <AccountSettings setIsClosable={setIsClosable} isClosable={isClosable} />
          </Modal>
          <CloseConfirmationDialog
            handleConfirmCancelClick={handleConfirmCancelClick}
            handleConfirmDiscardClick={handleConfirmDiscardClick}
            isConfirmDialogOpen={isConfirmDialogOpen}
          />
        </>
      )}
      <Modal
        name="qrModal"
        isModalOpen={isQRModalOpen}
        onClose={closeQRModal}
        title={t('globalHelp.downloadAppText')}
      >
        <QRModalWrapper>
          <QRDescription>
            {t('globalHelp.downloadAppDescription')}
          </QRDescription>
          <QRCodeWrapper>
            <QRCodeSVG value={downloadAppQrCodeUrl} />
          </QRCodeWrapper>
        </QRModalWrapper>
      </Modal>
      <ContactUsModalAlt
        isModalOpen={isContactUsModalOpen}
        closeModal={closeContactUsModal}
      />
      <Menu items={profileMenuItems} className={profileListStyles} aria-label="Profile menu" />
    </Container>
  );
};

ProfileMenu.propTypes = {
  setIsOpened: PropTypes.func,
};

export default ProfileMenu;
